import { useEffect, useState } from 'react';
import { fetchLeads, fetchCount } from '../lib/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (leads) => {
  const headers = [
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Referral Source',
    'Consent',
    'Source Page',
    'IP',
    'User Agent',
    'Submitted',
  ];
  const rows = leads.map((l) => [
    l.firstName,
    l.lastName,
    l.email,
    l.phone,
    l.referralSource || '',
    l.smsEmailConsent ? 'Yes' : 'No',
    l.sourcePage || '',
    l.ipAddress || '',
    l.userAgent || '',
    l.createdAt || '',
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map(csvEscape).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  a.href = url;
  a.download = `hall-pass-leads-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([fetchLeads(), fetchCount()])
      .then(([l, c]) => {
        setLeads(l.leads || []);
        setCount(c);
      })
      .catch((e) => setError(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="hp-admin">
      <header className="hp-admin-header">
        <div>
          <p className="hp-eyebrow">
            <span className="hp-dot" />
            Admin · The Hall Pass
          </p>
          <h1 className="hp-admin-title">Reservations</h1>
        </div>
        <div className="hp-admin-stats">
          <div>
            <div className="hp-admin-stat-label">Total counter</div>
            <div className="hp-admin-stat-value" data-testid="admin-count">
              {count}
            </div>
          </div>
          <div>
            <div className="hp-admin-stat-label">Submissions</div>
            <div className="hp-admin-stat-value" data-testid="admin-leads-total">
              {leads.length}
            </div>
          </div>
          <button
            type="button"
            className="hp-admin-export"
            onClick={() => downloadCsv(leads)}
            disabled={leads.length === 0}
            data-testid="admin-export-csv"
          >
            Export CSV
          </button>
        </div>
      </header>

      {loading ? (
        <p className="hp-admin-empty">Loading…</p>
      ) : error ? (
        <p className="hp-admin-empty">{error}</p>
      ) : leads.length === 0 ? (
        <p className="hp-admin-empty">No reservations yet.</p>
      ) : (
        <div className="hp-table-wrap">
          <table className="hp-table" data-testid="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Referral</th>
                <th>Consent</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td>{l.firstName} {l.lastName}</td>
                  <td>{l.email}</td>
                  <td>{l.phone}</td>
                  <td>{l.referralSource || '—'}</td>
                  <td>{l.smsEmailConsent ? 'Yes' : 'No'}</td>
                  <td>{fmt(l.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
